import { useState, useEffect } from 'react';
import { fmt } from '../../utils/format';
import axios from 'axios';

const fmtM = (v) => v == null ? '—' : `$${(v / 1e6).toLocaleString('en-US', { maximumFractionDigits: 2 })} M`;
const fmtPct = (v) => v == null ? '—' : `${(v * 100).toFixed(2)}%`;

export default function AdamKhooDCF({ adamKhoo, currentPrice, onRecalc, symbol }) {
  const [loading, setLoading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [autoLoaded, setAutoLoaded] = useState(false);
  const [inputs, setInputs] = useState(() => ({
    growthRate1_5:   ((adamKhoo?.inputs?.growthRate1_5  ?? 0.10) * 100).toFixed(2),
    growthRate6_10:  ((adamKhoo?.inputs?.growthRate6_10 ?? 0.07) * 100).toFixed(2),
    growthRate11_20: ((adamKhoo?.inputs?.growthRate11_20 ?? 0.04) * 100).toFixed(2),
    discountRate:    ((adamKhoo?.inputs?.discountRate   ?? 0.09) * 100).toFixed(2),
  }));

  const set = (k, v) => setInputs(p => ({ ...p, [k]: v }));

  const suggestFromAnalysts = async () => {
    if (!symbol) return;
    setSuggesting(true);
    try {
      const { data } = await axios.get(`/api/stocks/${symbol}/growth-estimates`);
      if (data.phase1 != null) {
        setSuggestion(data);
        setInputs(p => ({
          ...p,
          growthRate1_5: data.phase1.toFixed(1),
          growthRate6_10: data.phase2.toFixed(1),
          growthRate11_20: data.phase3.toFixed(1),
        }));
      } else {
        setSuggestion({ source: null, disclaimer: data.disclaimer });
      }
    } catch {
      setSuggestion({ source: null, disclaimer: 'Could not fetch analyst estimates.' });
    } finally { setSuggesting(false); }
  };

  useEffect(() => {
    if (symbol && !autoLoaded) {
      setAutoLoaded(true);
      suggestFromAnalysts();
    }
  }, [symbol]);

  const recalc = async () => {
    setLoading(true);
    await onRecalc({
      growthRate1_5:   parseFloat(inputs.growthRate1_5)   / 100,
      growthRate6_10:  parseFloat(inputs.growthRate6_10)  / 100,
      growthRate11_20: parseFloat(inputs.growthRate11_20) / 100,
      discountRate:    parseFloat(inputs.discountRate)    / 100,
    });
    setLoading(false);
  };

  if (!adamKhoo) return (
    <div className="card">
      <div className="card-title">3-Phase DCF Valuation</div>
      <div style={{ color: 'var(--text-muted)', padding: '20px 0' }}>Insufficient operating cash flow data for this stock.</div>
    </div>
  );

  const { inputs: inp, projections, pvOf20YrCF, intrinsicBeforeCashDebt, debtPerShare, cashPerShare, intrinsicValue } = adamKhoo;
  const discountPremium = currentPrice && intrinsicValue ? ((currentPrice - intrinsicValue) / intrinsicValue) * 100 : null;
  const isUndervalued = discountPremium != null && discountPremium < 0;
  const verdictColor = discountPremium == null ? 'var(--text-muted)' : discountPremium < -20 ? 'var(--green)' : discountPremium < 0 ? 'var(--green)' : discountPremium < 20 ? 'var(--yellow)' : 'var(--red)';

  const phase1 = projections?.filter(p => p.phase === 1) || [];
  const phase2 = projections?.filter(p => p.phase === 2) || [];
  const phase3 = projections?.filter(p => p.phase === 3) || [];

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div className="card-title" style={{ margin: 0 }}>3-Phase 20-Year DCF</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>3-phase 20-year discounted cash flow model</div>
        </div>
      </div>

      {/* Input Panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Left: Stock Data */}
        <div style={{ background: 'var(--bg-surface)', borderRadius: 6, padding: 16, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stock Data (Auto-filled)</div>
          {[
            ['Operating Cash Flow (Current)', fmtM(inp?.operatingCF)],
            ['Total Debt (ST + LT)', fmtM(inp?.totalDebt)],
            ['Cash & Short-term Investments', fmtM(inp?.cashAndInvest)],
            ['Shares Outstanding', inp?.sharesOut ? `${(inp.sharesOut / 1e6).toFixed(1)} M` : '—'],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)' }}>{label}</span>
              <span style={{ fontFamily: 'JetBrains Mono', color: 'var(--text-primary)' }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Right: Assumptions */}
        <div style={{ background: 'var(--bg-surface)', borderRadius: 6, padding: 16, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Growth Assumptions</div>
          {[
            ['Cash Flow Growth Yr 1–5 (%)', 'growthRate1_5', 'Phase 1 — High growth'],
            ['Cash Flow Growth Yr 6–10 (%)', 'growthRate6_10', 'Phase 2 — Moderate growth'],
            ['Cash Flow Growth Yr 11–20 (%)', 'growthRate11_20', 'Phase 3 — Terminal growth'],
            ['Discount Rate (%)', 'discountRate', 'Required rate of return'],
          ].map(([label, key, hint]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{hint}</span>
              </div>
              <input type="number" step="0.5" className="input" style={{ padding: '6px 10px', fontSize: 13 }}
                value={inputs[key]} onChange={e => set(key, e.target.value)} />
            </div>
          ))}
          <button className="btn" onClick={suggestFromAnalysts} disabled={suggesting} style={{ width: '100%', marginTop: 4, marginBottom: 6, background: 'var(--bg-hover)', border: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: 12 }}>
            {suggesting ? 'Fetching estimates...' : '💡 Suggest from analyst estimates'}
          </button>
          {suggestion && (
            <div style={{ fontSize: 11, color: suggestion.source ? 'var(--text-muted)' : 'var(--yellow)', padding: '6px 0', lineHeight: 1.4 }}>
              {suggestion.source
                ? <>Source: <span style={{ color: 'var(--accent)' }}>{suggestion.source}</span>. {suggestion.disclaimer}</>
                : suggestion.disclaimer}
            </div>
          )}
          <button className="btn btn-primary" onClick={recalc} disabled={loading} style={{ width: '100%', marginTop: 4 }}>
            {loading ? 'Calculating...' : 'Recalculate'}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div style={{ background: 'var(--bg-surface)', borderRadius: 6, padding: 16, border: '1px solid var(--border)', marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Valuation Results</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
          <div className="stat-cell">
            <div className="stat-label">PV of 20-Yr Cash Flows</div>
            <div className="stat-value" style={{ fontSize: 13 }}>{fmtM(pvOf20YrCF)}</div>
          </div>
          <div className="stat-cell">
            <div className="stat-label">Intrinsic Value (pre-cash/debt)</div>
            <div className="stat-value">{fmt.currency(intrinsicBeforeCashDebt)}</div>
          </div>
          <div className="stat-cell">
            <div className="stat-label">Less: Debt per Share</div>
            <div className="stat-value" style={{ color: 'var(--red)' }}>− {fmt.currency(debtPerShare)}</div>
          </div>
          <div className="stat-cell">
            <div className="stat-label">Plus: Cash per Share</div>
            <div className="stat-value" style={{ color: 'var(--green)' }}>+ {fmt.currency(cashPerShare)}</div>
          </div>
          <div className="stat-cell" style={{ border: '1px solid var(--accent)', background: 'var(--accent-dim)' }}>
            <div className="stat-label">Final Intrinsic Value / Share</div>
            <div className="stat-value" style={{ fontSize: 20, color: 'var(--accent)' }}>{fmt.currency(intrinsicValue)}</div>
          </div>
          <div className="stat-cell">
            <div className="stat-label">Last Close</div>
            <div className="stat-value">{fmt.currency(currentPrice)}</div>
            <div style={{ fontSize: 11, marginTop: 4, color: verdictColor, fontWeight: 600 }}>
              {discountPremium != null ? `${isUndervalued ? 'Discount' : 'Premium'}: ${Math.abs(discountPremium).toFixed(2)}%` : '—'}
            </div>
          </div>
        </div>
        {discountPremium != null && (
          <div style={{ background: isUndervalued ? 'var(--green-dim)' : 'var(--red-dim)', border: `1px solid ${isUndervalued ? 'var(--green)' : 'var(--red)'}`, borderRadius: 6, padding: '10px 16px', fontSize: 13, textAlign: 'center' }}>
            <span style={{ fontWeight: 700, color: isUndervalued ? 'var(--green)' : 'var(--red)', marginRight: 8 }}>
              {isUndervalued ? '✓ Trading at a Discount — Potentially Undervalued' : '✗ Trading at a Premium — Potentially Overvalued'}
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>
              ({discountPremium.toFixed(2)}% vs intrinsic value)
            </span>
          </div>
        )}
      </div>

      {/* Projection Table */}
      <div>
        <div className="card-title">20-Year Cash Flow Projection</div>
        {[
          { label: 'Phase 1 — High Growth', color: '#3b82f6', data: phase1, rate: inputs.growthRate1_5 },
          { label: 'Phase 2 — Moderate Growth', color: '#22c55e', data: phase2, rate: inputs.growthRate6_10 },
          { label: 'Phase 3 — Terminal Growth', color: '#a855f7', data: phase3, rate: inputs.growthRate11_20 },
        ].map(({ label, color, data, rate }) => data.length > 0 && (
          <div key={label} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: 12, fontWeight: 600, color }}>{label}</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({rate}% p.a.)</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: 600 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Year</th>
                    {data.map(p => <th key={p.year}>{p.year}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ color: 'var(--text-secondary)', fontFamily: 'inherit' }}>Operating CF (Projected)</td>
                    {data.map(p => <td key={p.year} style={{ color }}>${(p.projectedCF / 1e6).toLocaleString('en-US', { maximumFractionDigits: 0 })}M</td>)}
                  </tr>
                  <tr>
                    <td style={{ color: 'var(--text-secondary)', fontFamily: 'inherit' }}>Discount Factor</td>
                    {data.map(p => <td key={p.year}>{p.discountFactor.toFixed(4)}</td>)}
                  </tr>
                  <tr style={{ background: 'var(--bg-hover)' }}>
                    <td style={{ fontWeight: 600, fontFamily: 'inherit' }}>Discounted Value</td>
                    {data.map(p => <td key={p.year} style={{ fontWeight: 600 }}>${(p.discountedValue / 1e6).toLocaleString('en-US', { maximumFractionDigits: 0 })}M</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
